import { useEffect } from 'react'
import { useAppStore } from '@core/store'
import { setupDoEstado } from './store/clashSlice'
import { useAuth } from '@core/auth/useAuth'
import { modulosNaInterface } from '@core/partilhas/partilhasRegras'
import { whenRecoveryResolved } from '@core/session/recovery'
import { resolveClashGuids } from './clashBcfExport'
import { comOrigem } from './clashAlteracaoOrigem'
import { classificacaoVazia } from './clashAgrupamentoEstado'
import { temDecisao } from './clashTriage'
import { persistTudo, loadSetup } from './clashTriageStorage'
import { MotorDaPartilha } from './clashPartilhaMotor'
import { abrirCanalWebSocket } from './clashPartilhaCanal'
import { actualizarModelos, criarTriagem, type ProjetoDeConflitos } from './clashPartilhaPorta'
import {
  gravarActiva,
  lerActiva,
  useClashPartilha,
  type TriagemActiva,
} from './clashPartilhaEstado'

// ---------------------------------------------------------------------------
// Quem liga e desliga a triagem partilhada: um motor por browser, para a
// triagem activa do workspace, enquanto houver uma superficie dos
// conflitos aberta (o painel do ribbon e a pagina do modulo partilham-no,
// como partilham o store).
//
// Entrar, partilhar e sair sao os tres gestos do utilizador:
//   - partilhar: cria a triagem no workspace com o codigo escolhido e
//     envia a deste browser;
//   - entrar: troca a triagem deste browser pela partilhada (pergunta
//     antes, se houver pareceres);
//   - sair: deixa de sincronizar; a copia local fica.
// ---------------------------------------------------------------------------

export interface ContextoDaPartilha {
  workspaceId: string
  utilizadorId: string
  nome: string
}

let motor: MotorDaPartilha | null = null
let contexto: ContextoDaPartilha | null = null
let superficies = 0

function definir(p: Parameters<ReturnType<typeof useClashPartilha.getState>['definir']>[0]): void {
  useClashPartilha.getState().definir(p)
}

function arrancar(ctx: ContextoDaPartilha, t: TriagemActiva): MotorDaPartilha {
  if (motor && motor.projetoId === t.projetoId) return motor
  motor?.parar()
  const m = new MotorDaPartilha({
    projetoId: t.projetoId,
    codigo: t.codigo,
    eu: { id: ctx.utilizadorId, nome: ctx.nome },
    abrirCanal: abrirCanalWebSocket,
    resolverGuids: resolveClashGuids,
    aoAviso: (texto) => useAppStore.getState().pushToast(texto, { type: 'info', timeout: 8000 }),
    aoParar: (motivo) => {
      sairDaTriagem()
      definir({ erro: motivo })
      useAppStore.getState().pushToast(motivo, { type: 'error' })
    },
    aoLimpar: () => sairDaTriagem(),
    aoCorrida: (c) => {
      // Melhor esforco: a lista das triagens mostra os modelos da ultima
      // corrida. Falhar aqui nao afecta a triagem.
      void actualizarModelos(
        t.projetoId,
        c.modelos.map((x) => ({ ficheiro: x.ficheiro, sha256: x.sha256 ?? '' })),
      ).catch(() => {})
    },
    aoEstado: (e) => definir({ ...e }),
  })
  motor = m
  definir({ activa: t, erro: null })
  m.iniciar()
  return m
}

/** Liga o motor se este workspace tem uma triagem partilhada activa. */
function ligarSeActiva(ctx: ContextoDaPartilha): void {
  contexto = ctx
  const t = lerActiva(ctx.workspaceId)
  if (!t) {
    definir({ activa: null })
    return
  }
  arrancar(ctx, t)
}

/** O motor em curso (para a interface). */
export function motorDaPartilha(): MotorDaPartilha | null {
  return motor
}

/** Deixa de sincronizar. A triagem local fica como esta. */
export function sairDaTriagem(): void {
  motor?.parar()
  motor = null
  if (contexto) gravarActiva(contexto.workspaceId, null)
  definir({ activa: null, estado: 'parada', presentes: [], pendentes: 0, semCorrespondencia: 0, semIdentidade: 0 })
}

/** Modelos abertos, para a triagem partilhada os lembrar. */
function modelosAbertos(): { ficheiro: string; sha256: string }[] {
  return useAppStore.getState().models.map((m) => ({ ficheiro: m.fileName, sha256: m.sha256 ?? '' }))
}

/** Partilha a triagem deste browser: por omissão só para quem partilha
 *  (que escolhe a seguir pessoas ou o workspace em "Partilhar com..."),
 *  ou já com o workspace inteiro. */
export async function partilharTriagem(
  codigo: string,
  nome: string | null,
  workspaceInteiro = false,
): Promise<ProjetoDeConflitos> {
  if (!contexto) throw new Error('A triagem partilhada precisa de sessão num workspace.')
  const ctx = contexto
  const p = await criarTriagem({
    codigo,
    nome,
    modelos: modelosAbertos(),
    workspace_inteiro: workspaceInteiro,
  })
  // O identificador do projecto BCF passa a ser o da triagem partilhada:
  // quem exportar dela tem os mesmos Guids de topico.
  useAppStore.getState().garantirProjectoBcf()
  const t: TriagemActiva = { projetoId: p.id, codigo: p.codigo, nome: p.nome }
  gravarActiva(ctx.workspaceId, t)
  const m = arrancar(ctx, t)
  // Os GlobalIds dos resultados em memoria: as entradas triadas antes de
  // a corrida os ter guardado passam a ter identidade.
  await m.casarResultados()
  m.carregarTudo()
  definir({ oferta: null })
  return p
}

/** Quantos pareceres tem a triagem deste browser. */
export function pareceresLocais(): number {
  return Object.values(useAppStore.getState().clashTriage).filter(temDecisao).length
}

/** A triagem desta sessão, para "Partilhar sessão" (sem a superfície dos
 *  conflitos aberta): a partilhada em que este browser está, e quantos
 *  pareceres há na triagem local. Lê a triagem do armazenamento se
 *  ainda não estiver em memória. */
export async function triagemDaSessao(
  workspaceId: string,
): Promise<{ activa: TriagemActiva | null; pareceres: number }> {
  await whenRecoveryResolved()
  const st = useAppStore.getState()
  st.hydrateClashTriage()
  st.hydrateClashSetup()
  return { activa: lerActiva(workspaceId), pareceres: pareceresLocais() }
}

/**
 * Partilha a triagem deste browser a partir de "Partilhar sessão": como
 * `partilharTriagem`, só para quem partilha (a sessão propaga-lhe depois
 * a lista dela), mas sem precisar da superfície dos conflitos aberta.
 * Sem superfície, o motor fica só até enviar a triagem e depois pára;
 * quando o painel abrir, volta a ligar-se sozinho (a triagem fica
 * lembrada como activa).
 */
export async function partilharTriagemParaSessao(
  ctx: ContextoDaPartilha,
  codigo: string,
): Promise<ProjetoDeConflitos> {
  await whenRecoveryResolved()
  const st = useAppStore.getState()
  st.hydrateClashTriage()
  st.hydrateClashSetup()
  if (!contexto) contexto = ctx
  const p = await partilharTriagem(codigo, null, false)
  const m = motor
  if (superficies === 0 && m) {
    await m.sincronizar().catch(() => {})
    if (superficies === 0 && motor === m) {
      m.parar()
      motor = null
      definir({ estado: 'parada', presentes: [] })
    }
  }
  return p
}

/**
 * Entra numa triagem partilhada. A triagem deste browser e substituida
 * pela partilhada; `confirmar` e chamado antes, se houver pareceres
 * (cancelar nao muda nada).
 */
export function entrarNaTriagem(
  p: { id: string; codigo: string; nome: string | null },
  confirmar: (mensagem: string) => boolean,
): boolean {
  if (!contexto) return false
  const n = pareceresLocais()
  if (
    n > 0 &&
    !confirmar(
      `Entrar na triagem partilhada "${p.codigo}" substitui a triagem deste browser `
        + `(${n} parecer${n === 1 ? '' : 'es'}) pela do workspace. `
        + 'Se a quiser guardar, grave antes a sessão. Continuar?',
    )
  ) {
    return false
  }
  motor?.parar()
  motor = null
  const s = useAppStore.getState()
  const vazia = classificacaoVazia()
  // Substituicao: a triagem local nao e apagada no servidor.
  persistTudo(
    {},
    s.clashSetupHydrated ? setupDoEstado(s) : loadSetup(),
    { historico: [], projectoBcf: null, chavesUltimaCorrida: s.clashChavesUltima, modulo: s.clashModulo, ultimaExportacao: s.clashUltimaExportacao },
    undefined,
    vazia,
  )
  comOrigem('substituicao', () =>
    useAppStore.setState({
      clashTriage: {},
      clashTriageHydrated: true,
      clashClassificacao: vazia,
      clashHistorico: [],
      clashProjectoBcf: null,
      clashResumoCorrida: null,
    }),
  )
  const t: TriagemActiva = { projetoId: p.id, codigo: p.codigo, nome: p.nome }
  gravarActiva(contexto.workspaceId, t)
  arrancar(contexto, t)
  definir({ oferta: null })
  return true
}

/** O conflito em foco (bloqueio leve: so mostra, nao impede). */
export function editarConflito(key: string | null): void {
  motor?.editar(key)
}

/**
 * Liga a triagem partilhada enquanto a superficie dos conflitos esta
 * aberta, em modo de sessao com o modulo Conflitos. Desligar so quando
 * sai a ultima superficie, e depois de enviar o que estiver na fila.
 */
export function usePartilhaDaTriagem(active: boolean): void {
  const { mode, modules, partilhados, user, workspace } = useAuth()
  // Também para quem só tem triagens partilhadas de outro workspace: a
  // API e o canal verificam cada triagem.
  const disponivel =
    mode === 'cloud' && modulosNaInterface(modules, partilhados).includes('clash') && !!user && !!workspace
  const workspaceId = workspace?.id ?? ''
  const utilizadorId = user?.id ?? ''
  const nome = user?.name || user?.email || ''

  useEffect(() => {
    if (!disponivel || !active) return
    let vivo = true
    superficies += 1
    void whenRecoveryResolved().then(() => {
      if (!vivo) return
      // A triagem tem de estar lida antes de o motor a observar.
      const st = useAppStore.getState()
      st.hydrateClashTriage()
      st.hydrateClashSetup()
      if (!useAppStore.getState().clashTriageHydrated) return
      ligarSeActiva({ workspaceId, utilizadorId, nome })
    })
    const aoVoltarRede = () => motor?.retomar()
    window.addEventListener('online', aoVoltarRede)
    return () => {
      vivo = false
      window.removeEventListener('online', aoVoltarRede)
      superficies -= 1
      if (superficies > 0) return
      const m = motor
      if (!m) return
      // O que estiver na fila sai antes de desligar; se nao sair, fica
      // guardado e sai na proxima abertura.
      void m.sincronizar().finally(() => {
        if (superficies === 0 && motor === m) {
          m.parar()
          motor = null
          definir({ estado: 'parada', presentes: [] })
        }
      })
    }
  }, [disponivel, active, workspaceId, utilizadorId, nome])
}

/** O contexto actual (para a sessao saber se pode oferecer partilhar). */
export function partilhaDisponivel(): boolean {
  return contexto !== null
}
